import { createSelector } from '@reduxjs/toolkit'
import { TRootState } from 'store/types'
import { AvatarSource, AvatarVoiceTab } from 'store/types/aiAvatar'

const selector = (state: TRootState) => state.aiAvatar

const isLoadingTabData = createSelector(
  selector,
  state => state.isLoadingTabData
)

const generationParams = createSelector(
  selector,
  state => state.generationParams
)

const avatar = createSelector(generationParams, params => params.avatar)

const isAvatarLoading = createSelector(selector, state => state.isAvatarLoading)

const hasAvatar = createSelector(avatar, avatarData => !!avatarData)

const voice = createSelector(generationParams, params => params.voice)

const voiceLangCode = createSelector(voice, v => v.langCode)

const voiceSpeakerId = createSelector(voice, v => v.speakerId)

const voiceSpeed = createSelector(voice, v => v.speed)

const voiceClone = createSelector(selector, state => state.voiceClone)

const voiceCloneActiveTab = createSelector(voiceClone, vc => vc.activeTab)

const clonedVoice = createSelector(voiceClone, vc => vc.voice)

const hasClonedVoice = createSelector(clonedVoice, v => !!v)

const voiceCloneUploadStatus = createSelector(voiceClone, vc => vc.uploadStatus)

const generationClones = createSelector(
  selector,
  state => state.generationClones
)

// The cloned-voice audio URL to send as `refAudio` (replacing the catalog
// speaker id) on TTS-trial and generate-avatar requests — but only while the
// Voice clone sub-tab is active AND a cloned voice is attached. Back on Voice
// library (or after removing the clone) this is undefined and the speaker id
// is sent instead.
const activeRefAudio = createSelector(
  voiceCloneActiveTab,
  clonedVoice,
  (tab, cloned) => (tab === AvatarVoiceTab.CLONE ? cloned?.url : undefined)
)

// True while the Voice clone sub-tab is active with an upload still in flight.
// During that window `activeRefAudio` is not set yet, so a Generate click
// would silently fall back to the catalog `speakerId` even though the UI shows
// a clone upload in progress — the footer gates on this to prevent it.
const isActiveVoiceCloneUploading = createSelector(
  voiceCloneActiveTab,
  voiceCloneUploadStatus,
  (tab, status) => tab === AvatarVoiceTab.CLONE && status === 'uploading'
)

const quality = createSelector(generationParams, params => params.quality)

const speechText = createSelector(generationParams, params => params.speechText)

// The avatar's aspect-ratio override (e.g. '16:9' | '9:16'), or null when it
// should follow the project aspect ratio. Resolve the effective value with
// `resolveAvatarAspectRatio` (store/features/aiAvatar/utils).
const avatarAspectRatio = createSelector(
  generationParams,
  params => params.aspectRatio
)

const targetMediaItemId = createSelector(
  generationParams,
  params => params.targetMediaItemId
)

const publicAvatars = createSelector(selector, state => state.publicAvatars)

const publicAvatarsData = createSelector(publicAvatars, pa => pa.data)

const isPublicAvatarsLoading = createSelector(publicAvatars, pa => pa.isLoading)

// Gender of the currently selected avatar, resolved by matching the selected
// avatar id against the loaded public-avatar list. Only public avatars carry a
// gender, so this is null for uploaded / my-files avatars or when none is set.
// Used to keep the voice gender-matched when the language changes.
const selectedPublicAvatarGender = createSelector(
  avatar,
  publicAvatarsData,
  (avatarData, publicAvatars) => {
    if (!avatarData || avatarData.source !== AvatarSource.PUBLIC) return null
    const match = publicAvatars?.find(pa => String(pa.id) === avatarData.id)
    return match?.gender ?? null
  }
)

const avatarModels = createSelector(selector, state => state.avatarModels)

const avatarModelsData = createSelector(avatarModels, am => am.data)

const isAvatarModelsLoading = createSelector(avatarModels, am => am.isLoading)

const avatarModel = createSelector(avatarModelsData, data => data?.[0])
// Quality options for the picker. We currently expose a single model, so the
// available qualities are the first model's `possibleQualities`.
const avatarPossibleQualities = createSelector(
  avatarModel,
  data => data?.possibleQualities ?? []
)

const avatarModelOptions = createSelector(
  avatarModel,
  data => data?.options ?? []
)

const avatarModelOneSecondOption = createSelector(avatarModelOptions, options =>
  options.find(option => option.duration === 1)
)

// Backend-provided average render seconds keyed by video duration (seconds), for
// the currently selected quality — from each option's `generationAvgDurationSeconds`
// (the same field AI Video uses as its expected generation duration). Feeds the
// generation-progress render estimate: each avatar screen is a video of some
// duration, so its render time is this map's value for that duration. See
// `estimateAvatarGenerationPhases`.
//
// BACKEND CAVEAT (verified 2026-07-08 via ai-video-api `/models?origin=avatar-editor`):
// the backend currently returns a flat placeholder `100` for EVERY duration and
// quality (the `VIDEO_GENERATION_AVG_DURATION_SECONDS` fallback), so the estimate
// is only as good as this data. Real per-(duration, quality) values must be
// populated backend-side for the bar to track reality.
const avatarRenderSecondsByDuration = createSelector(
  [avatarModelOptions, quality],
  (options, currentQuality) => {
    const map = new Map<number, number>()
    for (const option of options) {
      const qualityOption = option.qualities.find(
        q => q.quality === currentQuality
      )
      if (qualityOption?.generationAvgDurationSeconds) {
        map.set(option.duration, qualityOption.generationAvgDurationSeconds)
      }
    }
    return map
  }
)

const giftAccess = createSelector(selector, state => state.giftAccess)

const giftAccessData = createSelector(giftAccess, ga => ga.data)

const isGiftAccessLoading = createSelector(giftAccess, ga => ga.isLoading)

const isGenerating = createSelector(selector, state => state.isGenerating)

const isAwaitingGiftRecheck = createSelector(
  selector,
  state => state.isAwaitingGiftRecheck
)

export const aiAvatarSelectors = {
  isLoadingTabData,
  generationParams,
  avatar,
  isAvatarLoading,
  hasAvatar,
  voice,
  voiceLangCode,
  voiceSpeakerId,
  voiceSpeed,
  voiceClone,
  voiceCloneActiveTab,
  clonedVoice,
  hasClonedVoice,
  voiceCloneUploadStatus,
  generationClones,
  activeRefAudio,
  isActiveVoiceCloneUploading,
  quality,
  speechText,
  avatarAspectRatio,
  targetMediaItemId,
  avatarModelOneSecondOption,
  avatarRenderSecondsByDuration,
  publicAvatars,
  publicAvatarsData,
  isPublicAvatarsLoading,
  selectedPublicAvatarGender,
  avatarModels,
  avatarModelsData,
  isAvatarModelsLoading,
  avatarPossibleQualities,
  giftAccess,
  giftAccessData,
  isGiftAccessLoading,
  isGenerating,
  isAwaitingGiftRecheck
}
